// Atemschutzüberwachung (AEF) Page - Respiratory Protection Monitoring
// Tablet-optimized real-time monitoring based on Berlin FwDV 7 logic
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Wind, Play, Square, AlertTriangle, Clock, Plus, Users, 
  CheckCircle, Timer, Gauge, Shield, ArrowLeft, History,
  Activity, Volume2, ChevronRight, User, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDate } from '../lib/utils';
import { useAuthStore } from '../lib/store';
import { atemschutzApi, usersApi, wacheApi } from '../lib/api';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

// =============================================
// Constants - Berlin FwDV 7 Logic
// =============================================

const STATUS_COLORS = {
  REGISTERED: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Registriert' },
  CONNECTED: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Angeschlossen' },
  ARRIVED: { bg: 'bg-green-100', text: 'text-green-700', label: 'Am Einsatzort' },
  RETREATING: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Rückzug' },
  FINISHED: { bg: 'bg-slate-200', text: 'text-slate-600', label: 'Beendet' },
  MAYDAY: { bg: 'bg-red-500', text: 'text-white', label: 'MAYDAY' },
};

const TRUPP_NAMES = [
  { value: 'Angriffstrupp', label: 'Angriffstrupp', short: 'AT' },
  { value: 'Wassertrupp', label: 'Wassertrupp', short: 'WT' },
  { value: 'Schlauchtrupp', label: 'Schlauchtrupp', short: 'ST' },
  { value: 'Sicherheitstrupp', label: 'Sicherheitstrupp', short: 'SiT' },
];

const GERAETE_TYPEN = [
  { value: 'PA30', label: 'PA 30 (1 Flasche)', zeit: '~30 min' },
  { value: 'PA60', label: 'PA 60 (2 Flaschen)', zeit: '~60 min' },
];

// =============================================
// Helper Functions
// =============================================

function formatSecondsToMinSec(seconds) {
  if (!seconds || seconds < 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

function formatDateTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function playNotificationSound() {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 880; // A5 note
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.15); // Short "bing"
  } catch (e) {
    console.log('Audio not available');
  }
}

// =============================================
// Countdown Hook - runs every second locally
// =============================================

function useCountdown(targetTime, onExpired) {
  const [remaining, setRemaining] = useState(() => {
    // Calculate initial value immediately
    if (!targetTime) return null;
    const target = new Date(targetTime).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((target - now) / 1000));
  });
  const expiredRef = useRef(false);
  
  useEffect(() => {
    if (!targetTime) {
      setRemaining(null);
      expiredRef.current = false;
      return;
    }
    
    const target = new Date(targetTime).getTime();
    
    // Calculate immediately, don't wait for first interval
    const now = Date.now();
    const initialDiff = Math.max(0, Math.floor((target - now) / 1000));
    setRemaining(initialDiff);
    
    const interval = setInterval(() => {
      const now = Date.now();
      const diff = Math.max(0, Math.floor((target - now) / 1000));
      setRemaining(diff);
      
      if (diff <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        onExpired?.();
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [targetTime, onExpired]);
  
  return remaining;
}

// =============================================
// Live Pressure Estimation Hook
// =============================================

function useLivePressure(anschlussZeit, anschlussDruck, fluchthaube, geraeteTyp) {
  const [pressure, setPressure] = useState(anschlussDruck);
  
  useEffect(() => {
    if (!anschlussZeit || !anschlussDruck) {
      setPressure(null);
      return;
    }
    
    const consumptionRate = fluchthaube ? 20 : 10; // bar per minute
    const bottleFactor = geraeteTyp === 'PA60' ? 2 : 1;
    const startTime = new Date(anschlussZeit).getTime();
    
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsedMinutes = (now - startTime) / 60000;
      const consumed = elapsedMinutes * consumptionRate / bottleFactor;
      const currentPressure = Math.max(0, Math.round(anschlussDruck - consumed));
      setPressure(currentPressure);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [anschlussZeit, anschlussDruck, fluchthaube, geraeteTyp]);
  
  return pressure;
}

// =============================================
// Trupp Card Component - Berlin FwDV 7 Logic
// =============================================

function TruppCard({ trupp, session, onStatusUpdate, onFluchthaube, onMayday, isOwner }) {
  const [showPressureDialog, setShowPressureDialog] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);
  const [pressure, setPressure] = useState('');
  const [abfrageSound1_3Played, setAbfrageSound1_3Played] = useState(false);
  const [abfrageSound2_3Played, setAbfrageSound2_3Played] = useState(false);
  
  const statusInfo = STATUS_COLORS[trupp.status] || STATUS_COLORS.REGISTERED;
  
  // Get calculated values from backend
  const arbeitsluft = trupp.arbeitsluft;
  const verbrauchHinweg = trupp.verbrauch_hinweg;
  const reserveRueckweg = trupp.reserve_rueckweg;
  const rueckzugBeiDruck = trupp.rueckzug_bei_druck;
  const sofortRueckzug = trupp.sofort_rueckzug;
  const vorAnkunft = trupp.vor_ankunft;
  
  // Get pressure events
  const anschlussDruck = trupp.druck_events?.anschluss?.druck || 0;
  const anschlussZeit = trupp.druck_events?.anschluss?.zeit || null;
  const ankunftDruck = trupp.druck_events?.ankunft?.druck || null;
  
  // Live pressure estimation (calculated locally every second)
  const livePressure = useLivePressure(
    anschlussZeit, 
    anschlussDruck, 
    trupp.fluchthaube_aktiv, 
    trupp.geraete_typ
  );
  
  // Use live pressure if available, fallback to server value
  const geschaetzterDruck = livePressure ?? trupp.geschaetzter_aktueller_druck ?? anschlussDruck;
  
  // Live countdowns - using ISO timestamps from backend
  const retreatTime = trupp.berechnet_rueckzug_zeit;
  const zeit0Bar = trupp.berechnet_0bar_zeit;
  const zeit1_3Abfrage = trupp.zeit_1_3_abfrage;
  const zeit2_3Abfrage = trupp.zeit_2_3_abfrage;
  const naechsteAbfrage = trupp.naechste_abfrage;
  
  // Main countdown: Zeit bis Rückzug
  const retreatCountdown = useCountdown(retreatTime, () => {
    if (trupp.status !== 'RETREATING' && trupp.status !== 'FINISHED') {
      playNotificationSound();
      playNotificationSound(); // Double for urgency
      toast.error(`${trupp.trupp_name}: RÜCKZUG EINLEITEN!`, { duration: 15000 });
    }
  });
  
  // 1/3 Abfrage countdown
  const abfrage1_3Countdown = useCountdown(zeit1_3Abfrage, () => {
    if (!abfrageSound1_3Played && trupp.status !== 'RETREATING' && trupp.status !== 'FINISHED') {
      setAbfrageSound1_3Played(true);
      playNotificationSound();
      toast.warning(`${trupp.trupp_name}: 1/3 Abfrage fällig! Erwarteter Druck: ~${trupp.druck_bei_1_3} bar`, { duration: 10000 });
    }
  });
  
  // 2/3 Abfrage countdown
  const abfrage2_3Countdown = useCountdown(zeit2_3Abfrage, () => {
    if (!abfrageSound2_3Played && trupp.status !== 'RETREATING' && trupp.status !== 'FINISHED') {
      setAbfrageSound2_3Played(true);
      playNotificationSound();
      toast.warning(`${trupp.trupp_name}: 2/3 Abfrage fällig! Erwarteter Druck: ~${trupp.druck_bei_2_3} bar`, { duration: 10000 });
    }
  });
  
  // 0-bar countdown (for reference)
  const countdown0Bar = useCountdown(zeit0Bar, null);
  
  // Pressure bar percentage (max 300 bar)
  const pressurePercent = Math.min(100, (geschaetzterDruck / 300) * 100);
  
  // Determine next action based on status
  const getNextAction = () => {
    switch (trupp.status) {
      case 'REGISTERED':
        return { status: 'CONNECTED', label: 'Anschluss', icon: Play, needsPressure: true };
      case 'CONNECTED':
        return { status: 'ARRIVED', label: 'Am Einsatzort', icon: CheckCircle, needsPressure: true };
      case 'ARRIVED':
        return { status: 'RETREATING', label: 'Rückzug', icon: ArrowLeft, needsPressure: true };
      case 'RETREATING':
        return { status: 'FINISHED', label: 'Beenden', icon: Square, needsPressure: true };
      default:
        return null;
    }
  };
  
  const nextAction = getNextAction();
  
  const handleActionClick = (action) => {
    if (action.needsPressure) {
      setTargetStatus(action.status);
      setPressure('');
      setShowPressureDialog(true);
    } else {
      onStatusUpdate(trupp.id, action.status);
    }
  };
  
  const handlePressureSubmit = () => {
    const pressureValue = parseInt(pressure, 10);
    // Start pressure must be at least 270 bar
    const minPressure = targetStatus === 'CONNECTED' ? 270 : 0;
    if (isNaN(pressureValue) || pressureValue < minPressure || pressureValue > 350) {
      toast.error(targetStatus === 'CONNECTED' 
        ? 'Startdruck muss zwischen 270 und 350 bar liegen' 
        : 'Ungültiger Druckwert (0-350 bar)');
      return;
    }
    onStatusUpdate(trupp.id, targetStatus, pressureValue);
    setShowPressureDialog(false);
  };
  
  // Warning states
  const isRetreatWarning = retreatCountdown !== null && retreatCountdown < 120 && retreatCountdown > 0 && trupp.status !== 'RETREATING' && trupp.status !== 'FINISHED';
  const isRetreatCritical = (retreatCountdown !== null && retreatCountdown <= 0 || sofortRueckzug) && trupp.status !== 'RETREATING' && trupp.status !== 'FINISHED';
  const isAbfrageFaellig = (abfrage1_3Countdown !== null && abfrage1_3Countdown <= 0 && abfrage2_3Countdown > 0) 
    || (abfrage2_3Countdown !== null && abfrage2_3Countdown <= 0 && retreatCountdown > 0);
  
  return (
    <>
      <Card className={cn(
        'relative overflow-hidden transition-all',
        trupp.mayday_aktiv && 'ring-4 ring-red-500 animate-pulse',
        isRetreatCritical && !trupp.mayday_aktiv && 'ring-4 ring-red-400 animate-pulse',
        isRetreatWarning && !trupp.mayday_aktiv && !isRetreatCritical && 'ring-2 ring-amber-400',
        isAbfrageFaellig && !isRetreatWarning && !isRetreatCritical && 'ring-2 ring-blue-400'
      )}>
        {/* Header with status */}
        <CardHeader className={cn('pb-2', statusInfo.bg)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <CardTitle className="text-lg">{trupp.trupp_name}</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={cn(statusInfo.bg, statusInfo.text, 'text-xs')}>
                {statusInfo.label}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {trupp.geraete_typ}
              </Badge>
            </div>
          </div>
          
          {/* Members */}
          <div className="flex flex-wrap gap-1 mt-2">
            {trupp.mitglieder?.map((m, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                <User className="w-3 h-3 mr-1" />
                {m.funktion && <span className="font-bold mr-1">{m.funktion}:</span>}
                {m.name}
              </Badge>
            ))}
          </div>
        </CardHeader>
        
        <CardContent className="pt-4 space-y-4">
          {/* MAIN TIMERS - Only show when squad is connected or arrived */}
          {trupp.status !== 'REGISTERED' && trupp.status !== 'FINISHED' && (
            <div className="space-y-3">
              {/* Primary Timer Row: Nächste Abfrage + Rückzug */}
              <div className="grid grid-cols-2 gap-3">
                {/* NÄCHSTE ABFRAGE - Most important for monitoring */}
                <div className={cn(
                  'p-3 rounded-lg text-center border-2',
                  naechsteAbfrage ? (
                    (naechsteAbfrage === '1/3' ? abfrage1_3Countdown : abfrage2_3Countdown) <= 0 ? 'bg-blue-100 border-blue-500 animate-pulse' :
                    (naechsteAbfrage === '1/3' ? abfrage1_3Countdown : abfrage2_3Countdown) < 60 ? 'bg-blue-50 border-blue-400' :
                    'bg-slate-50 border-slate-200'
                  ) : 'bg-slate-50 border-slate-200'
                )}>
                  <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                    <Clock className="w-3 h-3" />
                    {naechsteAbfrage ? `${naechsteAbfrage} Abfrage` : 'Abfragen erledigt'}
                  </div>
                  <div className={cn(
                    'text-2xl font-mono font-bold',
                    naechsteAbfrage && (naechsteAbfrage === '1/3' ? abfrage1_3Countdown : abfrage2_3Countdown) <= 0 ? 'text-blue-600' :
                    naechsteAbfrage && (naechsteAbfrage === '1/3' ? abfrage1_3Countdown : abfrage2_3Countdown) < 60 ? 'text-blue-500' :
                    'text-slate-600'
                  )}>
                    {naechsteAbfrage === '1/3' ? 
                      (abfrage1_3Countdown !== null && abfrage1_3Countdown > 0 ? formatSecondsToMinSec(abfrage1_3Countdown) : 'JETZT!') :
                      naechsteAbfrage === '2/3' ?
                      (abfrage2_3Countdown !== null && abfrage2_3Countdown > 0 ? formatSecondsToMinSec(abfrage2_3Countdown) : 'JETZT!') :
                      '✓'
                    }
                  </div>
                  {naechsteAbfrage && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Erwartet: ~{naechsteAbfrage === '1/3' ? trupp.druck_bei_1_3 : trupp.druck_bei_2_3} bar
                    </div>
                  )}
                </div>
                
                {/* RÜCKZUG COUNTDOWN - Critical safety timer */}
                <div className={cn(
                  'p-3 rounded-lg text-center border-2',
                  isRetreatCritical ? 'bg-red-100 border-red-500 animate-pulse' :
                  isRetreatWarning ? 'bg-amber-100 border-amber-400' :
                  'bg-green-50 border-green-200'
                )}>
                  <div className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                    <ArrowLeft className="w-3 h-3" />
                    {vorAnkunft ? '1/3 Zeit (= Rückzug vor Ankunft)' : 'Zeit bis Rückzug'}
                  </div>
                  <div className={cn(
                    'text-2xl font-mono font-bold',
                    isRetreatCritical ? 'text-red-600' :
                    isRetreatWarning ? 'text-amber-600' :
                    'text-green-600'
                  )}>
                    {isRetreatCritical ? 'JETZT!' : 
                      retreatCountdown !== null ? formatSecondsToMinSec(retreatCountdown) : '--:--'}
                  </div>
                  {vorAnkunft ? (
                    <div className="text-xs text-muted-foreground mt-1">
                      Ankunft melden für genaue Berechnung
                    </div>
                  ) : rueckzugBeiDruck ? (
                    <div className="text-xs text-muted-foreground mt-1">
                      Bei ~{rueckzugBeiDruck} bar
                    </div>
                  ) : null}
                </div>
              </div>
              
              {/* Secondary Info Row: Pressure + 0-bar time */}
              <div className="grid grid-cols-3 gap-2">
                {/* Geschätzter aktueller Druck - LIVE */}
                <div className="p-2 rounded bg-slate-50 text-center">
                  <div className="text-[10px] text-muted-foreground">~Druck jetzt</div>
                  <div className="text-lg font-mono font-semibold text-slate-700">
                    {geschaetzterDruck > 0 ? geschaetzterDruck : '--'} bar
                  </div>
                </div>
                
                {/* Arbeitsluft (nur wenn Ankunft bestätigt) */}
                {!vorAnkunft && ankunftDruck && (
                  <div className="p-2 rounded bg-slate-50 text-center">
                    <div className="text-[10px] text-muted-foreground">Arbeitsluft</div>
                    <div className="text-lg font-mono font-semibold text-slate-700">
                      {arbeitsluft != null ? arbeitsluft : '--'} bar
                    </div>
                  </div>
                )}
                
                {/* 0-bar Zeit */}
                <div className="p-2 rounded bg-slate-50 text-center">
                  <div className="text-[10px] text-muted-foreground">0-bar in</div>
                  <div className="text-lg font-mono font-semibold text-slate-500">
                    {countdown0Bar !== null ? formatSecondsToMinSec(countdown0Bar) : '--:--'}}
                  </div>
                </div>
              </div>
              
              {/* Calculation Details (when arrived) */}
              {ankunftDruck && (
                <div className="text-xs text-muted-foreground bg-slate-50 p-2 rounded flex flex-wrap gap-x-4 gap-y-1">
                  <span>Anschluss: <strong>{anschlussDruck} bar</strong></span>
                  <span>Ankunft: <strong>{ankunftDruck} bar</strong></span>
                  <span>Hinweg: <strong>{verbrauchHinweg} bar</strong></span>
                  <span>Reserve (2×): <strong>{reserveRueckweg} bar</strong></span>
                </div>
              )}
            </div>
          )}
          
          {/* Pressure Bar */}
          {geschaetzterDruck > 0 && trupp.status !== 'REGISTERED' && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Geschätzter Druck</span>
                <span>~{geschaetzterDruck} / 300 bar</span>
              </div>
              <Progress 
                value={pressurePercent} 
                className={cn(
                  'h-2',
                  pressurePercent < 30 ? '[&>div]:bg-red-500' :
                  pressurePercent < 50 ? '[&>div]:bg-amber-500' :
                  '[&>div]:bg-green-500'
                )}
              />
            </div>
          )}
          
          {/* Fluchthaube Toggle */}
          {trupp.status !== 'FINISHED' && isOwner && (
            <Button
              variant={trupp.fluchthaube_aktiv ? 'default' : 'outline'}
              size="sm"
              className={cn(
                'w-full',
                trupp.fluchthaube_aktiv && 'bg-amber-500 hover:bg-amber-600'
              )}
              onClick={() => onFluchthaube(trupp.id, !trupp.fluchthaube_aktiv)}
            >
              <Wind className="w-4 h-4 mr-2" />
              Fluchthaube {trupp.fluchthaube_aktiv ? 'aktiv (2x Verbrauch)' : 'aktivieren'}
            </Button>
          )}
          
          {/* Timestamps */}
          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            {trupp.druck_events?.anschluss?.zeit && (
              <div>Anschluss: {formatDateTime(trupp.druck_events.anschluss.zeit)} ({trupp.druck_events.anschluss.druck} bar)</div>
            )}
            {trupp.druck_events?.ankunft?.zeit && (
              <div>Ankunft: {formatDateTime(trupp.druck_events.ankunft.zeit)} ({trupp.druck_events.ankunft.druck} bar)</div>
            )}
            {trupp.druck_events?.rueckzug?.zeit && (
              <div>Rückzug: {formatDateTime(trupp.druck_events.rueckzug.zeit)} ({trupp.druck_events.rueckzug.druck} bar)</div>
            )}
            {trupp.druck_events?.ende?.zeit && (
              <div>Ende: {formatDateTime(trupp.druck_events.ende.zeit)} ({trupp.druck_events.ende.druck} bar)</div>
            )}
          </div>
          
          {/* Action Buttons */}
          {isOwner && trupp.status !== 'FINISHED' && (
            <div className="flex gap-2">
              {nextAction && (
                <Button
                  size="lg"
                  className="flex-1 h-14 text-lg"
                  onClick={() => handleActionClick(nextAction)}
                >
                  <nextAction.icon className="w-5 h-5 mr-2" />
                  {nextAction.label}
                </Button>
              )}
              
              {!trupp.mayday_aktiv && (
                <Button
                  size="lg"
                  variant="destructive"
                  className="h-14"
                  onClick={() => onMayday(trupp.id)}
                >
                  <AlertTriangle className="w-5 h-5" />
                </Button>
              )}
            </div>
          )}
          
          {/* MAYDAY Warning */}
          {trupp.mayday_aktiv && (
            <div className="p-4 bg-red-500 text-white rounded-lg text-center">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 animate-bounce" />
              <div className="text-2xl font-bold">MAYDAY</div>
              <div className="text-sm opacity-90">Notfall ausgelöst</div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Pressure Input Dialog */}
      <Dialog open={showPressureDialog} onOpenChange={setShowPressureDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Druckwert eingeben</DialogTitle>
            <DialogDescription>
              Geben Sie den aktuellen Flaschendruck ein.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <Label className="w-24">Druck (bar)</Label>
              <Input
                type="number"
                min={targetStatus === 'CONNECTED' ? 270 : 0}
                max="350"
                value={pressure}
                onChange={(e) => setPressure(e.target.value)}
                className="text-2xl font-mono h-14 text-center"
                placeholder={targetStatus === 'CONNECTED' ? '300' : '200'}
                autoFocus
              />
            </div>
            
            {/* Quick Select Buttons - different values for start vs. other states */}
            <div className="flex gap-2 flex-wrap">
              {targetStatus === 'CONNECTED' ? (
                // Start pressure: only 270-300 allowed
                [300, 290, 280, 270].map((val) => (
                  <Button
                    key={val}
                    variant="outline"
                    size="sm"
                    onClick={() => setPressure(val.toString())}
                  >
                    {val}
                  </Button>
                ))
              ) : (
                // Other states: full range
                [250, 200, 150, 100, 80, 60, 50, 30].map((val) => (
                  <Button
                    key={val}
                    variant="outline"
                    size="sm"
                    onClick={() => setPressure(val.toString())}
                  >
                    {val}
                  </Button>
                ))
              )}
            </div>
            
            {/* Info for start pressure */}
            {targetStatus === 'CONNECTED' && (
              <div className="text-xs text-muted-foreground">
                Hinweis: Startdruck muss mindestens 270 bar sein.
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPressureDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handlePressureSubmit}>
              Bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// =============================================
// New Session Dialog
// =============================================

function NewSessionDialog({ open, onOpenChange, onCreate }) {
  const [einsatzort, setEinsatzort] = useState('');
  const [einsatznummer, setEinsatznummer] = useState('');
  const [kanal, setKanal] = useState('');
  
  const handleCreate = () => {
    if (!einsatzort.trim()) {
      toast.error('Einsatzort ist erforderlich');
      return;
    }
    onCreate({
      einsatzort: einsatzort.trim(),
      einsatznummer: einsatznummer.trim() || null,
      kanal: kanal.trim() || null,
    });
    setEinsatzort('');
    setEinsatznummer('');
    setKanal('');
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Neue Atemschutzüberwachung</DialogTitle>
          <DialogDescription>
            Starten Sie eine neue Überwachungssession.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Einsatzort *</Label>
            <Input
              value={einsatzort}
              onChange={(e) => setEinsatzort(e.target.value)}
              placeholder="z.B. Musterstraße 1, Keller"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Einsatznummer</Label>
            <Input
              value={einsatznummer}
              onChange={(e) => setEinsatznummer(e.target.value)}
              placeholder="optional"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Funkkanal</Label>
            <Input
              value={kanal}
              onChange={(e) => setKanal(e.target.value)}
              placeholder="optional, z.B. 4m/2"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleCreate}>
            <Play className="w-4 h-4 mr-2" />
            Starten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================
// Add Trupp Dialog with Personnel Selection
// =============================================

function AddTruppDialog({ open, onOpenChange, onAdd, quickStartData }) {
  const [truppName, setTruppName] = useState('Angriffstrupp');
  const [geraeteTyp, setGeraeteTyp] = useState('PA30');
  const [mitglieder, setMitglieder] = useState([
    { name: '', funktion: 'ATF', user_id: null },
    { name: '', funktion: 'ATM', user_id: null },
  ]);
  const [searchTerm, setSearchTerm] = useState(['', '']);
  
  // Get available personnel from quick start data
  const verfuegbareMitarbeiter = quickStartData?.verfuegbare_mitarbeiter || [];
  
  // Pre-fill from quick start data (duty roster)
  useEffect(() => {
    if (quickStartData && open) {
      if (truppName === 'Angriffstrupp' && quickStartData.angriffstrupp) {
        const at = quickStartData.angriffstrupp;
        setMitglieder(at.mitglieder.map(m => ({
          name: m.name,
          funktion: m.funktion,
          user_id: m.user_id
        })));
      } else if (truppName === 'Wassertrupp' && quickStartData.wassertrupp) {
        const wt = quickStartData.wassertrupp;
        setMitglieder(wt.mitglieder.map(m => ({
          name: m.name,
          funktion: m.funktion,
          user_id: m.user_id
        })));
      } else {
        // Reset to empty if no pre-fill data
        const func1 = truppName === 'Angriffstrupp' ? 'ATF' : 
                      truppName === 'Wassertrupp' ? 'WTF' : 'TF';
        const func2 = truppName === 'Angriffstrupp' ? 'ATM' : 
                      truppName === 'Wassertrupp' ? 'WTM' : 'TM';
        setMitglieder([
          { name: '', funktion: func1, user_id: null },
          { name: '', funktion: func2, user_id: null },
        ]);
      }
    }
  }, [quickStartData, truppName, open]);
  
  // Update functions when trupp name changes
  useEffect(() => {
    const funcMap = {
      'Angriffstrupp': ['ATF', 'ATM'],
      'Wassertrupp': ['WTF', 'WTM'],
      'Schlauchtrupp': ['STF', 'STM'],
      'Sicherheitstrupp': ['SiTF', 'SiTM'],
    };
    const funcs = funcMap[truppName] || ['TF', 'TM'];
    setMitglieder(prev => prev.map((m, i) => ({
      ...m,
      funktion: funcs[i] || funcs[0]
    })));
  }, [truppName]);
  
  const handleAdd = () => {
    const validMembers = mitglieder.filter(m => m.name.trim());
    if (validMembers.length === 0) {
      toast.error('Mindestens ein Truppmann erforderlich');
      return;
    }
    onAdd({
      trupp_name: truppName,
      geraete_typ: geraeteTyp,
      mitglieder: validMembers.map(m => ({
        name: m.name.trim(),
        funktion: m.funktion,
        user_id: m.user_id
      }))
    });
    onOpenChange(false);
  };
  
  const updateMember = (index, field, value) => {
    setMitglieder(prev => prev.map((m, i) => 
      i === index ? { ...m, [field]: value } : m
    ));
  };
  
  const selectMitarbeiter = (index, mitarbeiter) => {
    setMitglieder(prev => prev.map((m, i) => 
      i === index ? { 
        ...m, 
        name: mitarbeiter.name, 
        user_id: mitarbeiter.user_id 
      } : m
    ));
    // Clear search
    setSearchTerm(prev => prev.map((s, i) => i === index ? '' : s));
  };
  
  const updateSearchTerm = (index, value) => {
    setSearchTerm(prev => prev.map((s, i) => i === index ? value : s));
    // Also update the name field
    updateMember(index, 'name', value);
    updateMember(index, 'user_id', null);
  };
  
  // Filter personnel by search term
  const getFilteredMitarbeiter = (index) => {
    const term = searchTerm[index]?.toLowerCase() || '';
    if (!term) return [];
    return verfuegbareMitarbeiter
      .filter(m => m.name.toLowerCase().includes(term))
      .slice(0, 8);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Trupp hinzufügen</DialogTitle>
          <DialogDescription>
            Fügen Sie einen neuen Trupp zur Überwachung hinzu.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Trupp</Label>
              <Select value={truppName} onValueChange={setTruppName}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRUPP_NAMES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Gerätetyp</Label>
              <Select value={geraeteTyp} onValueChange={setGeraeteTyp}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GERAETE_TYPEN.map(g => (
                    <SelectItem key={g.value} value={g.value}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Truppmann 1 (Führer) */}
          <div className="space-y-2">
            <Label>Truppmann 1 (Führer)</Label>
            <div className="flex gap-2">
              <Input
                className="w-16"
                value={mitglieder[0]?.funktion || ''}
                onChange={(e) => updateMember(0, 'funktion', e.target.value)}
                placeholder="Fkt"
              />
              <div className="flex-1 relative">
                <Input
                  value={mitglieder[0]?.name || ''}
                  onChange={(e) => updateSearchTerm(0, e.target.value)}
                  placeholder="Name eingeben oder suchen..."
                  className={mitglieder[0]?.user_id ? 'border-green-500 bg-green-50' : ''}
                />
                {/* Autocomplete dropdown */}
                {searchTerm[0] && getFilteredMitarbeiter(0).length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                    {getFilteredMitarbeiter(0).map((m) => (
                      <button
                        key={m.user_id}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 flex items-center justify-between"
                        onClick={() => selectMitarbeiter(0, m)}
                      >
                        <span>{m.name}</span>
                        {m.ist_im_dienst && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                            {m.funktion_aktuell || 'im Dienst'}
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Truppmann 2 */}
          <div className="space-y-2">
            <Label>Truppmann 2</Label>
            <div className="flex gap-2">
              <Input
                className="w-16"
                value={mitglieder[1]?.funktion || ''}
                onChange={(e) => updateMember(1, 'funktion', e.target.value)}
                placeholder="Fkt"
              />
              <div className="flex-1 relative">
                <Input
                  value={mitglieder[1]?.name || ''}
                  onChange={(e) => updateSearchTerm(1, e.target.value)}
                  placeholder="Name eingeben oder suchen..."
                  className={mitglieder[1]?.user_id ? 'border-green-500 bg-green-50' : ''}
                />
                {/* Autocomplete dropdown */}
                {searchTerm[1] && getFilteredMitarbeiter(1).length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                    {getFilteredMitarbeiter(1).map((m) => (
                      <button
                        key={m.user_id}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 flex items-center justify-between"
                        onClick={() => selectMitarbeiter(1, m)}
                      >
                        <span>{m.name}</span>
                        {m.ist_im_dienst && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                            {m.funktion_aktuell || 'im Dienst'}
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Quick Start Hint */}
          {quickStartData && (quickStartData.angriffstrupp || quickStartData.wassertrupp) && (
            <div className="text-xs text-muted-foreground bg-blue-50 p-2 rounded">
              <Activity className="w-3 h-3 inline mr-1" />
              Daten aus der aktuellen Diensteinteilung wurden vorausgefüllt.
            </div>
          )}
          
          {/* Available personnel info */}
          {verfuegbareMitarbeiter.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <Users className="w-3 h-3 inline mr-1" />
              {verfuegbareMitarbeiter.length} Mitarbeiter verfügbar. Tippen Sie zum Suchen.
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleAdd}>
            <Plus className="w-4 h-4 mr-2" />
            Hinzufügen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================
// History View
// =============================================

function HistoryView() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  
  const { data: historyData, isLoading } = useQuery({
    queryKey: ['aef-history'],
    queryFn: () => atemschutzApi.getHistory({ pro_seite: 50 }).then(r => r.data),
  });
  
  const deleteSessionMutation = useMutation({
    mutationFn: (id) => atemschutzApi.deleteSession(id),
    onSuccess: () => {
      toast.success('Session gelöscht');
      queryClient.invalidateQueries(['aef-history']);
      setDeleteConfirmId(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  });
  
  // Check if user can delete (WACHABTEILUNGSLEITUNG and above)
  const canDelete = user?.rolle && ['DEVELOPER', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'WACHABTEILUNGSLEITUNG'].includes(user.rolle);
  
  const formatTimeOnly = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };
  
  const formatDateFull = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
        <p className="mt-4 text-muted-foreground">Lade Historie...</p>
      </div>
    );
  }
  
  const sessions = historyData?.sessions || [];
  
  if (sessions.length === 0) {
    return (
      <div className="p-8 text-center">
        <History className="w-12 h-12 mx-auto text-muted-foreground opacity-50" />
        <h3 className="mt-4 font-medium">Keine Überwachungen</h3>
        <p className="text-sm text-muted-foreground">
          Es wurden noch keine Atemschutzüberwachungen durchgeführt.
        </p>
      </div>
    );
  }
  
  return (
    <>
      <ScrollArea className="h-[500px]">
        <div className="space-y-2 p-2">
          {sessions.map((session) => {
            const isExpanded = expandedId === session.id;
            
            return (
              <Card key={session.id} className="overflow-hidden">
                <CardContent className="p-0">
                  {/* Header Row - clickable to expand */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-muted/50 flex items-center justify-between"
                    onClick={() => setExpandedId(isExpanded ? null : session.id)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-medium">{session.einsatzort}</div>
                        <Badge variant={session.status === 'FINISHED' ? 'secondary' : 'default'} className="text-xs">
                          {session.status === 'FINISHED' ? 'Beendet' : 'Aktiv'}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 mt-1">
                        <span>{formatDateFull(session.erstellt_am)}</span>
                        {session.einsatznummer && <span>Einsatznr.: {session.einsatznummer}</span>}
                        {session.kanal && <span>Kanal: {session.kanal}</span>}
                        <span>{session.ersteller_name}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{session.anzahl_trupps} Trupp(s)</Badge>
                      <ChevronRight className={cn(
                        "w-5 h-5 text-muted-foreground transition-transform",
                        isExpanded && "rotate-90"
                      )} />
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t bg-slate-50">
                      {/* Trupp Details */}
                      {session.trupps?.length > 0 ? (
                        <div className="mt-3 space-y-3">
                          {session.trupps.map((trupp, idx) => (
                            <div key={idx} className="bg-white rounded-lg p-3 border">
                              <div className="flex items-center justify-between mb-2">
                                <div className="font-medium flex items-center gap-2">
                                  <Shield className="w-4 h-4" />
                                  {trupp.trupp_name}
                                  <Badge variant="outline" className="text-xs">{trupp.geraete_typ}</Badge>
                                </div>
                                {trupp.mayday_ausgeloest && (
                                  <Badge variant="destructive">MAYDAY</Badge>
                                )}
                              </div>
                              
                              {/* Members */}
                              <div className="text-sm text-muted-foreground mb-2">
                                {trupp.mitglieder_namen?.join(', ') || 'Keine Mitglieder'}
                              </div>
                              
                              {/* Pressure and Time Grid */}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                {trupp.start_druck && (
                                  <div className="bg-slate-50 rounded p-2">
                                    <div className="text-muted-foreground">Start</div>
                                    <div className="font-mono font-semibold">{trupp.start_druck} bar</div>
                                    {trupp.anschluss_zeit && (
                                      <div className="text-muted-foreground">{formatTimeOnly(trupp.anschluss_zeit)}</div>
                                    )}
                                  </div>
                                )}
                                {trupp.ankunft_zeit && (
                                  <div className="bg-slate-50 rounded p-2">
                                    <div className="text-muted-foreground">Ankunft</div>
                                    <div className="font-mono font-semibold">{formatTimeOnly(trupp.ankunft_zeit)}</div>
                                  </div>
                                )}
                                {trupp.rueckzug_zeit && (
                                  <div className="bg-slate-50 rounded p-2">
                                    <div className="text-muted-foreground">Rückzug</div>
                                    <div className="font-mono font-semibold">{formatTimeOnly(trupp.rueckzug_zeit)}</div>
                                  </div>
                                )}
                                {trupp.end_druck && (
                                  <div className="bg-slate-50 rounded p-2">
                                    <div className="text-muted-foreground">Ende</div>
                                    <div className="font-mono font-semibold">{trupp.end_druck} bar</div>
                                    {trupp.ende_zeit && (
                                      <div className="text-muted-foreground">{formatTimeOnly(trupp.ende_zeit)}</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-3 text-sm text-muted-foreground">
                          Keine Trupp-Details verfügbar.
                        </div>
                      )}
                      
                      {/* Delete Button for leadership */}
                      {canDelete && session.status === 'FINISHED' && (
                        <div className="mt-4 pt-3 border-t flex justify-end">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirmId(session.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Session löschen
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </ScrollArea>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Session löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diese Atemschutzüberwachung wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteSessionMutation.mutate(deleteConfirmId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// =============================================
// Main Page Component
// =============================================

export default function AtemschutzPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  
  const [showNewSession, setShowNewSession] = useState(false);
  const [showAddTrupp, setShowAddTrupp] = useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('monitor');
  
  // Fetch active session
  const { data: activeSession, isLoading: sessionLoading, refetch: refetchSession } = useQuery({
    queryKey: ['aef-active-session'],
    queryFn: () => atemschutzApi.getMyActiveSession().then(r => r.data),
    refetchInterval: (data) => (data?.status === 'ACTIVE' ? 5000 : false), // Poll every 5s when active
  });
  
  // Fetch quick start data
  const { data: quickStartData } = useQuery({
    queryKey: ['aef-quick-start'],
    queryFn: () => atemschutzApi.getQuickStart().then(r => r.data),
    enabled: showAddTrupp,
  });
  
  // Mutations
  const createSessionMutation = useMutation({
    mutationFn: (data) => atemschutzApi.createSession(data),
    onSuccess: () => {
      toast.success('Überwachung gestartet');
      queryClient.invalidateQueries(['aef-active-session']);
      setShowNewSession(false);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Fehler beim Starten');
    }
  });
  
  const addTruppMutation = useMutation({
    mutationFn: (data) => atemschutzApi.addTrupp(activeSession.id, data),
    onSuccess: () => {
      toast.success('Trupp hinzugefügt');
      queryClient.invalidateQueries(['aef-active-session']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  });
  
  const updateStatusMutation = useMutation({
    mutationFn: ({ truppId, status, druck }) => 
      atemschutzApi.updateTruppStatus(activeSession.id, truppId, { 
        neuer_status: status, 
        druck 
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['aef-active-session']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Fehler beim Statusupdate');
    }
  });
  
  const fluchtHaubeMutation = useMutation({
    mutationFn: ({ truppId, aktiv }) => 
      atemschutzApi.toggleFluchthaube(activeSession.id, truppId, aktiv),
    onSuccess: (_, vars) => {
      toast.success(vars.aktiv ? 'Fluchthaube aktiviert' : 'Fluchthaube deaktiviert');
      queryClient.invalidateQueries(['aef-active-session']);
    }
  });
  
  const maydayMutation = useMutation({
    mutationFn: (truppId) => atemschutzApi.triggerMayday(activeSession.id, truppId),
    onSuccess: () => {
      playNotificationSound();
      toast.error('MAYDAY ausgelöst!', { duration: 10000 });
      queryClient.invalidateQueries(['aef-active-session']);
    }
  });
  
  const finishSessionMutation = useMutation({
    mutationFn: () => atemschutzApi.finishSession(activeSession.id),
    onSuccess: () => {
      toast.success('Überwachung beendet');
      queryClient.invalidateQueries(['aef-active-session']);
      queryClient.invalidateQueries(['aef-history']);
      setShowFinishConfirm(false);
    }
  });
  
  const handleStatusUpdate = (truppId, status, druck) => {
    updateStatusMutation.mutate({ truppId, status, druck });
  };
  
  const handleFluchthaube = (truppId, aktiv) => {
    fluchtHaubeMutation.mutate({ truppId, aktiv });
  };
  
  const handleMayday = (truppId) => {
    // Confirm before triggering MAYDAY
    if (window.confirm('MAYDAY wirklich auslösen? Dies signalisiert einen Notfall.')) {
      maydayMutation.mutate(truppId);
    }
  };
  
  const isOwner = activeSession?.erstellt_von === user?.id;
  
  return (
    <MainLayout>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Gauge className="w-6 h-6 text-primary" />
              Atemschutzüberwachung
            </h1>
          </div>
          
          {!activeSession && (
            <Button onClick={() => setShowNewSession(true)} size="lg">
              <Play className="w-4 h-4 mr-2" />
              Neue Überwachung
            </Button>
          )}
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="monitor" className="gap-2">
              <Activity className="w-4 h-4" />
              Live-Überwachung
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="w-4 h-4" />
              Historie
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="monitor" className="mt-6">
            {sessionLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
                <p className="mt-4 text-muted-foreground">Lade...</p>
              </div>
            ) : !activeSession ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Shield className="w-16 h-16 mx-auto text-muted-foreground opacity-30" />
                  <h2 className="mt-4 text-xl font-medium">Keine aktive Überwachung</h2>
                  <p className="text-muted-foreground mt-2">
                    Starten Sie eine neue Atemschutzüberwachung, um Trupps zu registrieren und zu überwachen.
                  </p>
                  <Button className="mt-6" onClick={() => setShowNewSession(true)}>
                    <Play className="w-4 h-4 mr-2" />
                    Überwachung starten
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Session Info Bar */}
                <Card className="bg-slate-50">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <div className="font-medium text-lg">{activeSession.einsatzort}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-4">
                          {activeSession.einsatznummer && (
                            <span>Einsatznr.: {activeSession.einsatznummer}</span>
                          )}
                          {activeSession.kanal && (
                            <span>Kanal: {activeSession.kanal}</span>
                          )}
                          <span>
                            Gestartet: {formatDateTime(activeSession.erstellt_am)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {isOwner && activeSession.trupps?.length < 2 && (
                          <Button variant="outline" onClick={() => setShowAddTrupp(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Trupp hinzufügen
                          </Button>
                        )}
                        {isOwner && (
                          <Button 
                            variant="destructive" 
                            onClick={() => setShowFinishConfirm(true)}
                          >
                            <Square className="w-4 h-4 mr-2" />
                            Beenden
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Trupp Cards Grid */}
                {activeSession.trupps?.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {activeSession.trupps.map((trupp) => (
                      <TruppCard
                        key={trupp.id}
                        trupp={trupp}
                        session={activeSession}
                        onStatusUpdate={handleStatusUpdate}
                        onFluchthaube={handleFluchthaube}
                        onMayday={handleMayday}
                        isOwner={isOwner}
                      />
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <Users className="w-12 h-12 mx-auto text-muted-foreground opacity-50" />
                      <h3 className="mt-4 font-medium">Keine Trupps registriert</h3>
                      <p className="text-muted-foreground text-sm">
                        Fügen Sie den ersten Trupp zur Überwachung hinzu.
                      </p>
                      {isOwner && (
                        <Button className="mt-4" onClick={() => setShowAddTrupp(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Trupp hinzufügen
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="history" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Vergangene Überwachungen</CardTitle>
                <CardDescription>
                  Ihre bisherigen Atemschutzüberwachungen
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <HistoryView />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Dialogs */}
      <NewSessionDialog
        open={showNewSession}
        onOpenChange={setShowNewSession}
        onCreate={(data) => createSessionMutation.mutate(data)}
      />
      
      <AddTruppDialog
        open={showAddTrupp}
        onOpenChange={setShowAddTrupp}
        onAdd={(data) => addTruppMutation.mutate(data)}
        quickStartData={quickStartData}
      />
      
      {/* Finish Confirmation */}
      <AlertDialog open={showFinishConfirm} onOpenChange={setShowFinishConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Überwachung beenden?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie die aktuelle Atemschutzüberwachung wirklich beenden?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => finishSessionMutation.mutate()}
              className="bg-red-600 hover:bg-red-700"
            >
              Beenden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
